// Pure helpers for capability pickers (Microsoft 365, Google Workspace): each
// capability has levels, each level needs a set of OAuth scopes, and the
// granted scopes map back to picks. No React and no api client at runtime, so
// `node --test` covers it.

export interface CapabilityLevel {
  readonly id: string;
  // Short enough for a segmented control.
  readonly label: string;
  // What this level lets Boo do, one sentence.
  readonly summary: string;
  readonly scopes: ReadonlyArray<string>;
}

export interface Capability<Id extends string = string> {
  readonly id: Id;
  readonly name: string;
  readonly levels: ReadonlyArray<CapabilityLevel>;
}

// Whether a capability is on, and at which level (kept while off, so turning it
// back on restores the level).
export interface Choice {
  on: boolean;
  level: string;
}
export type Choices = Record<string, Choice>;

export const NO_SCOPES: ReadonlyArray<string> = [];

// What the granted scopes amount to: each capability at the highest level
// whose scopes are all granted, or off.
export function grantedChoices(caps: ReadonlyArray<Capability>, grantedScopes: ReadonlyArray<string>): Choices {
  const granted = new Set(grantedScopes);
  const out: Choices = {};
  for (const cap of caps) {
    let match: CapabilityLevel | null = null;
    for (const level of cap.levels) {
      if (level.scopes.every((s) => granted.has(s))) match = level;
    }
    out[cap.id] = { on: match !== null, level: (match ?? cap.levels[0]!).id };
  }
  return out;
}

export function levelOf(cap: Capability, choice: Choice): CapabilityLevel {
  return cap.levels.find((l) => l.id === choice.level) ?? cap.levels[0]!;
}

// The scopes to ask for, deduplicated.
export function scopesFor(caps: ReadonlyArray<Capability>, choices: Choices): string[] {
  const out = new Set<string>();
  for (const cap of caps) {
    const choice = choices[cap.id];
    if (choice?.on) for (const s of levelOf(cap, choice).scopes) out.add(s);
  }
  return [...out];
}

// The level of an off capability doesn't count.
export const sameChoice = (a: Choice, b: Choice) => a.on === b.on && (!a.on || a.level === b.level);

export const choicesDiffer = (caps: ReadonlyArray<Capability>, a: Choices, b: Choices) =>
  caps.some((c) => !sameChoice(a[c.id]!, b[c.id]!));

// Picks saved in the browser before leaving for the provider, checked field by
// field: anything missing or malformed falls back to off.
export function parseSavedChoices(caps: ReadonlyArray<Capability>, saved: unknown): Choices | null {
  const choices = (saved as { choices?: Partial<Record<string, Partial<Choice>>> } | null)?.choices;
  if (!choices || typeof choices !== "object") return null;
  const none = grantedChoices(caps, NO_SCOPES);
  const out: Choices = {};
  for (const cap of caps) {
    const c = choices[cap.id];
    out[cap.id] =
      c && typeof c.on === "boolean" && typeof c.level === "string" ? { on: c.on, level: c.level } : none[cap.id]!;
  }
  return out;
}
